import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ListVideo, ShieldAlert, Loader2, CheckCircle, Send } from 'lucide-react';
import api from '../../services/api';

export const ContentChangeRequestModal = ({ contentLists = [], onClose }) => {
  const [wantList, setWantList] = useState(false);
  const [wantAdult, setWantAdult] = useState(false);
  const [desiredTemplateId, setDesiredTemplateId] = useState(contentLists[0]?.template_id ?? null);
  const [desiredAdult, setDesiredAdult] = useState(true);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!wantList && !wantAdult) {
      setError('Sélectionnez au moins une modification.');
      return;
    }
    const request_type = wantList && wantAdult ? 'both' : wantList ? 'change_list' : 'toggle_adult';
    const chosen = contentLists.find((c) => c.template_id === desiredTemplateId);
    setLoading(true);
    try {
      await api.post('/client/content-change-request', {
        request_type,
        desired_template_id: wantList ? desiredTemplateId : null,
        desired_template_name: wantList ? chosen?.name : null,
        desired_is_adult: wantAdult ? desiredAdult : null,
        message,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de l'envoi de la demande");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] rounded-t-2xl sm:rounded-lg metallic-border w-full sm:max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="content-change-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <ListVideo className="h-5 w-5 text-[#FF6B35]" /> Demander une modification
          </h3>
          <button onClick={onClose} className="text-[#6B6B6E] hover:text-white p-2" data-testid="close-content-change">&#10005;</button>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="content-change-success">
            <CheckCircle className="h-14 w-14 text-[#00FF88] mx-auto mb-3" />
            <h4 className="text-white font-bold mb-1">Demande envoyée !</h4>
            <p className="text-[#B8B8BA] text-sm">Notre équipe traitera votre demande et mettra à jour votre accès. Vous serez notifié par email.</p>
            <button onClick={onClose} className="btn-primary mt-5 px-6 py-2.5" data-testid="content-change-close-success">Fermer</button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[#B8B8BA] text-sm">Choisissez ce que vous souhaitez modifier. La modification est appliquée manuellement par notre équipe.</p>

            {/* Changer la liste */}
            <button
              onClick={() => setWantList(!wantList)}
              className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${wantList ? 'border-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D]'}`}
              data-testid="want-change-list"
            >
              <span className="text-white text-sm flex items-center gap-2"><ListVideo className="h-4 w-4 text-[#FF6B35]" /> Changer ma liste de chaînes</span>
              <span className={`w-5 h-5 rounded border flex items-center justify-center ${wantList ? 'bg-[#FF6B35] border-[#FF6B35]' : 'border-[#6B6B6E]'}`}>
                {wantList && <CheckCircle className="h-4 w-4 text-black" />}
              </span>
            </button>
            {wantList && (
              <select
                value={desiredTemplateId ?? ''}
                onChange={(e) => setDesiredTemplateId(Number(e.target.value))}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none"
                data-testid="desired-list-select"
              >
                {contentLists.map((c) => (
                  <option key={c.template_id} value={c.template_id}>{c.name}</option>
                ))}
              </select>
            )}

            {/* Adulte */}
            <button
              onClick={() => setWantAdult(!wantAdult)}
              className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${wantAdult ? 'border-[#FF3B5C] bg-[#FF3B5C]/10' : 'border-[#3A3A3D]'}`}
              data-testid="want-toggle-adult"
            >
              <span className="text-white text-sm flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-[#FF3B5C]" /> Activer / désactiver les chaînes adultes</span>
              <span className={`w-5 h-5 rounded border flex items-center justify-center ${wantAdult ? 'bg-[#FF3B5C] border-[#FF3B5C]' : 'border-[#6B6B6E]'}`}>
                {wantAdult && <CheckCircle className="h-4 w-4 text-black" />}
              </span>
            </button>
            {wantAdult && (
              <div className="flex gap-2">
                <button onClick={() => setDesiredAdult(true)} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${desiredAdult ? 'bg-[#FF3B5C] text-white' : 'bg-[#222226] text-[#B8B8BA] border border-[#3A3A3D]'}`} data-testid="adult-on">Activer</button>
                <button onClick={() => setDesiredAdult(false)} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${!desiredAdult ? 'bg-[#3A3A3D] text-white' : 'bg-[#222226] text-[#B8B8BA] border border-[#3A3A3D]'}`} data-testid="adult-off">Désactiver</button>
              </div>
            )}

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              placeholder="Message (facultatif)"
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none"
              data-testid="content-change-message"
            />

            {error && <p className="text-[#FF3B5C] text-xs">{error}</p>}

            <button onClick={submit} disabled={loading} className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-content-change">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              <span>Envoyer la demande</span>
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ContentChangeRequestModal;
